import { Component, inject } from '@angular/core';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { DashboardService } from '../../../services/dashboard.service';
import { SummaryRequest } from '../../../../../_shared/models';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { FilterService } from '../../../../../_shared/services';
import { KpiWidgetComponent } from "../../../../../_shared/components/kpi-widget/kpi-widget.component";
import { TranslocoService } from '@jsverse/transloco';

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

@Component({
  selector: 'app-avg-workshop-downtime-widget',
  standalone: true,
  imports: [KpiWidgetComponent],
  templateUrl: './avg-workshop-downtime-widget.component.html'
})
export class AvgWorkshopDowntimeWidgetComponent extends BaseWidgetComponent {
  private readonly transloco = inject(TranslocoService);

  hasError: boolean = false;
  errorMessage: string = '';

  private latestRequestId = 0;
  private dashboardService = inject(DashboardService);
  private errorHandlerService = inject(ErrorHandlerService);

  constructor() {
    const filterService = inject(FilterService);
    super(filterService);
    this.title = this.transloco.translate('maintenanceDashboard.widgets.avgWorkshopDowntime');
    this.isLoading = true;
    this.value = 0;
    this.percentageDifference = 0;
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    const requestId = ++this.latestRequestId;
    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';

    try {
      this.cancelSubject.next();

      const request: SummaryRequest = {
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!
      };

      const response = await this.dashboardService.getAverageWorkshopDowntimeStats(
        request,
        this.cancelSubject
      );

      this.value = response?.data?.totalValue ?? 0;
      this.percentageDifference = response?.data?.percentageChange ?? 0;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: (errorMessage: string) => {
          this.errorMessage = errorMessage;
          this.hasError = true;
        }
      });
    } finally {
      if (requestId === this.latestRequestId) {
        this.isLoading = false;
      }
    }
  }

  public override formatValue(value: number): string {
    const seconds = (value ?? 0) * HOUR;
    const roundsTo = (divisor: number) => Math.round((seconds / divisor) * 10) / 10;

    if (roundsTo(1) < MINUTE) return this.formatDowntime(roundsTo(1), 'seconds');
    if (roundsTo(MINUTE) < 60) return this.formatDowntime(roundsTo(MINUTE), 'minutes');
    if (roundsTo(HOUR) < 24) return this.formatDowntime(roundsTo(HOUR), 'hour', 'hours');
    return this.formatDowntime(roundsTo(DAY), 'day', 'days');
  }

  private formatDowntime(amount: number, unitKey: string, pluralUnitKey: string = unitKey): string {
    const key = amount === 1 ? unitKey : pluralUnitKey;
    return this.transloco.translate(`maintenanceDashboard.widgets.avgWorkshopDowntimeUnits.${key}`, {
      value: amount.toLocaleString('default', { maximumFractionDigits: 1 })
    });
  }
}
